<template>
  <form role="form" @submit.prevent.stop>
    <payout-form-group :label="$t('Номер карты')"
                       :required="model.isRequiredAttribute('cardNumber')"
                       :has-errors="model.errors.has('cardNumber')">
      <payout-cardnumber-input class="form-control" name="cardNumber"
                               v-model="model.values.cardNumber"/>

      <payout-input-hint-block v-slot:hints
                               :hint="$t('cardnumber_hint')"></payout-input-hint-block>

      <template v-slot:hints>
        <payout-input-hint-block :force-show="true" v-if="model.options.cardCountries.length > 0">
          <i18n tag="span" path="card_countries_hint">
            <template v-slot:countries>{{model.options.cardCountries.map(c => $t('countries.alfa2.' + c.alpha2)).join(', ')}}</template>
          </i18n>
        </payout-input-hint-block>
      </template>

      <error-message-block v-slot:errors attribute="cardNumber|*"
                           :errors="model.errors"></error-message-block>
    </payout-form-group>

    <template v-if="isBeneficiaryRequired">
      <payout-form-group :label="$t('Срок действия карты')"
                         :required="model.isRequiredAttribute('expirationDate')"
                         :has-errors="model.errors.has('expirationDate')">
        <payout-date-input v-model="model.values.expirationDate"
                           ref="expirationDate"
                           :picker-options="expirationDatePickerOptions"
                           :init-value="model.values.expirationDate"
                           class="form-control"/>
        <payout-input-hint-block v-slot:hints
                                 :hint="$t('expiration_date_hint')"></payout-input-hint-block>
        <error-message-block v-slot:errors attribute="expirationDate"
                             :errors="model.errors"></error-message-block>
      </payout-form-group>


      <payout-form-group>
        <h3 style="width: 380px; text-align: center;">{{ $t('Получатель') }}</h3>
      </payout-form-group>

      <payout-textfield-input-group :model="model"
                                    :label="$t('Имя на карте')"
                                    attribute="cardholderName"></payout-textfield-input-group>
      <payout-textfield-input-group :model="model"
                                    :label="$t('Фамилия на карте')"
                                    attribute="cardholderSurname"></payout-textfield-input-group>

      <payout-form-group :label="$t('Дата рождения')"
                         :required="model.isRequiredAttribute('cardholderBirthdate')"
                         :has-errors="model.errors.has('cardholderBirthdate')">
        <payout-date-input v-model="model.values.cardholderBirthdate"
                           ref="cardholderBirthdate"
                           :picker-options="birthdatePickerOptions"
                           :init-value="model.values.cardholderBirthdate"
                           class="form-control"/>
        <payout-input-hint-block v-slot:hints
                                 :hint="$t('birthdate_hint')"></payout-input-hint-block>
        <error-message-block v-slot:errors attribute="cardholderBirthdate"
                             :errors="model.errors"></error-message-block>
      </payout-form-group>

      <payout-textfield-input-group :model="model"
                                    :label="$t('Адрес получателя')"
                                    attribute="cardholderAddress"></payout-textfield-input-group>
      <payout-textfield-input-group :model="model"
                                    :label="$t('Город')"
                                    attribute="cardholderAddressCity"></payout-textfield-input-group>

      <payout-form-group :label="$t('Страна')"
                         :required="model.isRequiredAttribute('cardholderAddressCountry')"
                         :has-errors="model.errors.has('cardholderAddressCountry')">

        <dropdown-keyvalue :options="cardholderAddressCountriesSorted"
                           :placeholder="$t('Выберите страну')"
                           :value="model.values.cardholderAddressCountry"
                           @input="model.values.cardholderAddressCountry = $event"></dropdown-keyvalue>

        <payout-input-hint-block v-slot:hints
                                 :hint="model.getHint('cardholderAddressCountry')"></payout-input-hint-block>
        <error-message-block v-slot:errors attribute="cardholderAddressCountry"
                             :errors="model.errors"></error-message-block>
      </payout-form-group>
    </template>

    <br/><br/>

    <payout-textfield-input-group :model="model"
                                  :label="$t('Назначение')"
                                  attribute="description"></payout-textfield-input-group>

    <payout-input-amount-form-group :model="model"></payout-input-amount-form-group>

    <payout-destination-currency-conversion-notice-form-group style="margin-top: 1em;"
                                              :model="model"></payout-destination-currency-conversion-notice-form-group>


    <payout-form-group>
      <div class="form-control-space">
        <payout-hint-panel :warning="true">
          {{$t('sngcards_description')}}
        </payout-hint-panel>
      </div>
    </payout-form-group>

    <payout-estimated-destination-amount-form-group :model="model"></payout-estimated-destination-amount-form-group>

    <payout-evaluate-fee-form-group :model="model"></payout-evaluate-fee-form-group>


    <payout-toggable-hint-panel-form-group v-if="model.options.limitsAlertText"
                                           :heading="$t('Лимиты')"
                                           :link-text="$t('Информация о лимитах')">
      <div v-html="model.options.limitsAlertText"></div>
    </payout-toggable-hint-panel-form-group>


    <payout-form-notification-block :model="model"></payout-form-notification-block>

    <hr/>
    <payout-group-verification-type :model="model"></payout-group-verification-type>
  </form>
</template>

<script lang="ts">
import AbstractPayoutForm from "@/components/forms/payout/abstract/AbstractPayoutForm.vue";
import {Component} from "vue-property-decorator";
import PayoutFormGroup from "@/components/forms/payout/PayoutFormGroup.vue";
import PayoutInputHintBlock from "@/components/forms/payout/PayoutInputHintBlock.vue";
import ErrorMessageBlock from "@/components/forms/payout/ErrorMessageBlock.vue";
import PayoutCardnumberInput from "@/components/forms/payout/forms/PayoutCardnumberInput.vue";
import PayoutDateInput from "@/components/forms/payout/forms/PayoutDateInput.vue";
import PayoutTextfieldInputGroup from "@/components/forms/payout/PayoutTextfieldInputGroup.vue";
import DropdownKeyvalue from "@/components/forms/dropdown/DropdownKeyValue.vue";
import PayoutInputAmountFormGroup from "@/components/forms/payout/PayoutInputAmountFormGroup.vue";
import PayoutEvaluateFeeFormGroup from "@/components/forms/payout/PayoutEvaluateFeeFormGroup.vue";
import PayoutToggableHintPanelFormGroup
  from "@/components/forms/payout/hint/PayoutToggableHintPanelFormGroup.vue";
import PayoutFormNotificationBlock
  from "@/components/forms/payout/forms/PayoutFormNotificationBlock.vue";
import PayoutGroupVerificationType
  from "@/components/payout/verification/PayoutGroupVerificationType.vue";
import {PayoutWebModelOptions} from "@/components/forms/payout/payout-basics";
import PayoutHintPanel from "@/components/forms/payout/PayoutHintPanel.vue";
import PayoutEstimatedDestinationAmountFormGroup
  from "@/components/forms/payout/forms/PayoutEstimatedDestinationAmountFormGroup.vue";
import {PaymentCodeEnum, PayoutWebModel} from "capitalist-shared/src/finance/payments";
import PayoutDestinationCurrencyConversionNoticeFormGroup
  from "@/components/forms/payout/crypto/PayoutDestinationCurrencyConversionNoticeFormGroup.vue";

interface SngCardsPaymentModelOptions extends PayoutWebModelOptions {
  countries: {}
  cardCountries: []
  beneficiaryRequired: boolean,
  limitsAlertText: string | null
}

export class SngCardsPaymentModel extends PayoutWebModel {
  systemName = PaymentCodeEnum.SngCards

  options: SngCardsPaymentModelOptions = {
    countries: {},
    cardCountries: [],
    beneficiaryRequired: false,
    limitsAlertText: null
  } as SngCardsPaymentModelOptions

  // values =  {
  //   cardholderBirthdate: '', cardholderBirthplace: '', cardholderName: '',
  //   cardholderSurname: '', cardholderAddress: '',
  //   expirationDate: '', cardholderAddressCity: '', cardholderAddressCountry: '',
  // }

  values = {
    ...this.getValues(),
    cardholderBirthdate: '', cardholderBirthplace: '', cardholderName: '',
    cardholderSurname: '', cardholderAddress: '',
    expirationDate: '', cardholderAddressCity: '', cardholderAddressCountry: '',
  }
}

@Component({
  name: "SngCardsPaymentForm",
  components: {
    PayoutDestinationCurrencyConversionNoticeFormGroup,
    PayoutEstimatedDestinationAmountFormGroup,
    PayoutHintPanel,
    PayoutGroupVerificationType,
    PayoutFormNotificationBlock,
    PayoutToggableHintPanelFormGroup,
    PayoutEvaluateFeeFormGroup,
    PayoutInputAmountFormGroup,
    DropdownKeyvalue,
    PayoutTextfieldInputGroup,
    PayoutDateInput,
    PayoutCardnumberInput, ErrorMessageBlock, PayoutInputHintBlock, PayoutFormGroup
  }
})
export default class SngCardsPaymentForm extends AbstractPayoutForm {
  model = new SngCardsPaymentModel()

  get expirationDatePickerOptions() {
    return {
      range: false,
      dateFormat: 'yyyy-MM',
      minDate: new Date(),
      maxDate: new Date(2060, 12, 31),
      view: 'years', minView: 'months',
      autoClose: true,
      language: this.$i18n.locale
      //language: //document.getElementsByTagName("html")[0].getAttribute("lang")
    }
  }

  get birthdatePickerOptions() {
    return {
      range: false,
      dateFormat: 'yyyy-MM-dd',
      minDate: new Date(1911, 0, 0),
      maxDate: new Date(),
      view: 'years', minView: 'days',
      autoClose: true,
      language: this.$i18n.locale
      //language: //document.getElementsByTagName("html")[0].getAttribute("lang")
    }
  }

  get isBeneficiaryRequired() {
    return this.model.options.beneficiaryRequired
  }

  get cardholderAddressCountries(): any {
    return this.model.loaded ? this.model.options.countries : {};
  }

  get cardholderAddressCountriesSorted(): any {
    return this.cardholderAddressCountries;
    // let countries: any = this.cardholderAddressCountries;
    //
    // let names = _.values(countries).sort((function (a, b) {
    //   return a.localeCompare(b);
    // }));
    // let v2k = _.invert(countries);
    // let result: any = {};
    //
    // for (let name of names) {
    //   result[v2k[name]] = name;
    // }
    //
    // return result;
  }

}
</script>

<style scoped>

</style>

<i18n>
{
  "en": {
    "Номер карты": "Card number",
    "Выберите страну": "Select country",
    "cardnumber_hint": "Card number. Example: 1234-5678-1234-5678",
    "Назначение": "Description",
    "Срок действия карты": "Expiration date",
    "expiration_date_hint": "Format: YYYY-MM",
    "Получатель": "Beneficiary",
    "Имя на карте": "Cardholder name",
    "Фамилия на карте": "Cardholder surname",
    "Дата рождения": "Birthdate",
    "birthdate_hint": "Format: YYYY-MM-DD",
    "Адрес получателя": "Beneficiary address",
    "Город": "City",
    "Страна": "Country",
    "Информация о лимитах": "Limits information",
    "Лимиты": "Limits",
    "card_countries_hint": "Only cards from the following countries are supported: {countries}.",
    "countries": {
      "alfa2": {
        "TM": "Turkmenistan",
        "KG": "Kyrgyzstan",
        "KZ": "Kazakhstan",
        "RU": "Russia",
        "AM": "Armenia",
        "GE": "Georgia",
        "UZ": "Uzbekistan",
        "BY": "Belarus",
        "AZ": "Azerbaijan",
        "MD": "Moldova",
        "TJ": "Tajikistan",
        "UA": "Ukraine"
      }
    },
    "sngcards_description": "Conversion to KZT is based on the USD/KZT rate. In the case of making a payment from a wallet other than USD, a preliminary conversion into USD will be made.",
    "sngcards_description_1": "This type of payment is intended for replenishment of Visa and Mastercard cards issued in USD. Payment execution currency: KZT, crediting takes place at the rate of the bank servicing the card.",
    "Информация о канале": "Channel information"
  },
  "ru": {
    "sngcards_description": "Конвертация в KZT происходит на базе курса USD/KZT. В случае оформления платежа с кошелька отличного от USD будет произведена предварительная конвертация в USD.",
    "sngcards_description_1": "Данный вид платежей предназначен для пополнения карт Visa и Mastercard, эмитированных в USD. Валюта исполнения платежей: KZT, зачисление происходит по курсу банка, обслуживающего карту.",
    "cardnumber_hint": "Номер карты. Пример: 1234-5678-1234-5678",
    "expiration_date_hint": "Формат: ГГГГ-ММ",
    "birthdate_hint": "Формат: ГГГГ-ММ-ДД",
    "card_countries_hint": "Поддерживаются только карты следующих стран: {countries}.",
    "countries": {
      "alfa2": {
        "TM": "Туркменистан",
        "KG": "Киргизия",
        "KZ": "Казахстан",
        "RU": "Россия",
        "AM": "Армения",
        "GE": "Грузия",
        "UZ": "Узбекистан",
        "BY": "Беларусь",
        "AZ": "Азербайджан",
        "MD": "Молдавия",
        "TJ": "Таджикистан",
        "UA": "Украина"
      }
    }
  }
}
</i18n>
